/**
 * ReplayViewer — 重播觀看器
 *
 * 顯示完整的遊戲重播，支援：
 * - 逐步播放/暫停
 * - 前進/後退事件
 * - 跳轉回合
 * - 速度控制 (1x/2x/4x)
 * - 事件列表面板
 * - 簡化的戰場視覺化
 */

import { useEffect, useRef, useCallback } from 'react';
import { useReplayStore } from '../../store/replayStore';
import { useUIStore } from '../../store/uiStore';
import type { StoredEvent, ReplayData } from '../../services/NetworkManager';

// ============================================================
// Event type → icon + Chinese description mapping
// ============================================================

const EVENT_TYPE_MAP: Record<string, { icon: string; label: string }> = {
  'CARD_SUMMON': { icon: '⚔️', label: '召喚' },
  'unit_summoned': { icon: '⚔️', label: '召喚' },
  'summon_unit': { icon: '⚔️', label: '召喚' },
  'CARD_DRAW': { icon: '🃏', label: '抽牌' },
  'cards_drawn': { icon: '🃏', label: '抽牌' },
  'draw': { icon: '🃏', label: '抽牌' },
  'COMBAT_RESULT': { icon: '⚡', label: '戰鬥' },
  'combat_result': { icon: '⚡', label: '戰鬥結算' },
  'COMBAT_START': { icon: '⚡', label: '戰鬥開始' },
  'combat_start': { icon: '⚡', label: '戰鬥開始' },
  'COMBAT_DAMAGE': { icon: '💥', label: '戰鬥傷害' },
  'SPELL_CAST': { icon: '✨', label: '施法' },
  'spell_cast': { icon: '✨', label: '施法' },
  'UNIT_DESTROYED': { icon: '💀', label: '破壞' },
  'unit_destroyed': { icon: '💀', label: '破壞' },
  'CARD_DESTROY': { icon: '💀', label: '破壞' },
  'PHASE_CHANGE': { icon: '📋', label: '階段' },
  'phase_changed': { icon: '📋', label: '階段' },
  'phase_change': { icon: '📋', label: '階段' },
  'TURN_START': { icon: '🔄', label: '回合開始' },
  'turn_start': { icon: '🔄', label: '回合開始' },
  'turn_started': { icon: '🔄', label: '回合開始' },
  'TURN_END': { icon: '🔄', label: '回合結束' },
  'turn_end': { icon: '🔄', label: '回合結束' },
  'GAME_START': { icon: '🎮', label: '遊戲開始' },
  'game_start': { icon: '🎮', label: '遊戲開始' },
  'game_started': { icon: '🎮', label: '遊戲開始' },
  'GAME_END': { icon: '🏁', label: '結束' },
  'game_end': { icon: '🏁', label: '遊戲結束' },
  'game_over': { icon: '🏁', label: '遊戲結束' },
  'ATTACK_DECLARE': { icon: '🗡️', label: '進攻宣言' },
  'attack_declared': { icon: '🗡️', label: '進攻宣言' },
  'EFFECT_TRIGGER': { icon: '💫', label: '效果觸發' },
  'effect_triggered': { icon: '💫', label: '效果觸發' },
  'BUFF_APPLY': { icon: '⬆️', label: '增益' },
  'buff_applied': { icon: '⬆️', label: '增益' },
  'CARD_MOVE': { icon: '↔️', label: '移動' },
  'unit_moved': { icon: '↔️', label: '移動' },
  'move_unit': { icon: '↔️', label: '移動' },
  'CARD_DISCARD': { icon: '🗑️', label: '棄牌' },
  'card_discarded': { icon: '🗑️', label: '棄牌' },
  'ENERGY_GAIN': { icon: '🔮', label: '魔力獲得' },
  'ENERGY_SPEND': { icon: '🔮', label: '魔力消耗' },
  'SCORE_UPDATE': { icon: '📊', label: '分數更新' },
  'score_updated': { icon: '📊', label: '分數更新' },
  'FIELD_MAGIC_PLACE': { icon: '🌟', label: '場地魔法' },
  'field_magic_placed': { icon: '🌟', label: '場地魔法' },
  'CARD_REVEAL': { icon: '👁️', label: '揭示' },
  'surrender': { icon: '🏳️', label: '投降' },
};

function getEventDisplay(eventType: string): { icon: string; label: string } {
  return EVENT_TYPE_MAP[eventType] ?? { icon: '📌', label: eventType };
}

// ============================================================
// Phase name mapping
// ============================================================

const PHASE_MAP: Record<string, string> = {
  'draw': '抽牌階段',
  'defense': '防禦階段',
  'summon': '召喚階段',
  'tactics': '戰術階段',
  'end': '結束階段',
  'DrawPhase': '抽牌階段',
  'DefensePhase': '防禦階段',
  'SummonPhase': '召喚階段',
  'TacticsPhase': '戰術階段',
  'EndPhase': '結束階段',
};

// ============================================================
// Win condition mapping
// ============================================================

const WIN_CONDITION_MAP: Record<string, string> = {
  'score': '分數勝利',
  'countdown': '倒數結束',
  'deckout': '牌庫耗盡',
  'surrender': '對手投降',
  'disconnect': '對手斷線',
};

// ============================================================
// Helper: format event description
// ============================================================

function formatEventDescription(event: StoredEvent, replayData: ReplayData | null): string {
  const { icon, label } = getEventDisplay(event.event_type);
  const data = event.event_data as Record<string, unknown> | null;
  const playerId = event.player_id;

  // Abbreviate player ID for display
  const p1Id = replayData?.players.player1_id ?? '';
  const p2Id = replayData?.players.player2_id ?? '';
  const playerLabel = playerId === p1Id ? 'P1' : playerId === p2Id ? 'P2' : (playerId ? playerId.slice(0, 6) : '');

  let detail = '';

  if (data) {
    // Try to extract useful info from event_data
    const cardName = (data.card_name ?? data.cardName ?? data.name ?? '') as string;
    const position = data.position as Record<string, unknown> | undefined;
    const posRow = position ? ((position.row as string | undefined) ?? (position.zone as string | undefined) ?? '') : '';
    const posCol = position ? String((position.column as number | undefined) ?? (position.col as number | undefined) ?? (position.index as number | undefined) ?? '') : '';
    const posStr = position ? `(${posRow}, ${posCol})` : '';
    const phase = data.phase as string | undefined;
    const newPhase = data.new_phase as string | undefined;

    switch (event.event_type) {
      case 'CARD_SUMMON':
      case 'unit_summoned':
      case 'summon_unit':
        detail = cardName ? `「${cardName}」${posStr ? ` 到 ${posStr}` : ''}` : '';
        break;
      case 'CARD_DRAW':
      case 'cards_drawn':
      case 'draw': {
        const count = (data.count ?? data.num_cards ?? 1) as number;
        detail = `${String(count)} 張`;
        break;
      }
      case 'COMBAT_RESULT':
      case 'combat_result': {
        const attacker = (data.attacker_name ?? data.attackerName ?? '') as string;
        const defender = (data.defender_name ?? data.defenderName ?? '') as string;
        const atkPower = String((data.attacker_atk as number | undefined) ?? (data.attackerAtk as number | undefined) ?? '');
        const defPower = String((data.defender_atk as number | undefined) ?? (data.defenderAtk as number | undefined) ?? '');
        detail = attacker && defender ? `${attacker}(${atkPower}) vs ${defender}(${defPower})` : '';
        break;
      }
      case 'SPELL_CAST':
      case 'spell_cast':
        detail = cardName ? `「${cardName}」` : '';
        break;
      case 'UNIT_DESTROYED':
      case 'unit_destroyed':
      case 'CARD_DESTROY':
        detail = cardName ? `「${cardName}」` : '';
        break;
      case 'PHASE_CHANGE':
      case 'phase_changed':
      case 'phase_change':
        detail = PHASE_MAP[newPhase ?? phase ?? ''] ?? (newPhase ?? phase ?? '');
        break;
      case 'TURN_START':
      case 'turn_start':
      case 'turn_started':
        detail = `第 ${String(event.turn_number)} 回合`;
        break;
      case 'GAME_END':
      case 'game_end':
      case 'game_over': {
        const winnerId = (data.winner_id ?? data.winnerId ?? '') as string;
        const winnerLabel = winnerId === p1Id ? 'P1' : winnerId === p2Id ? 'P2' : winnerId ? winnerId.slice(0, 6) : '';
        detail = winnerLabel ? `${winnerLabel} 勝利` : '平手';
        break;
      }
      case 'CARD_MOVE':
      case 'unit_moved':
      case 'move_unit':
        detail = cardName ? `「${cardName}」${posStr ? ` → ${posStr}` : ''}` : '';
        break;
      case 'ATTACK_DECLARE':
      case 'attack_declared': {
        const attackers = data.attackers as string[] | undefined;
        detail = attackers ? `${String(attackers.length)} 個單位` : '';
        break;
      }
      default:
        if (cardName) {
          detail = `「${cardName}」`;
        }
    }
  }

  const prefix = playerLabel ? `${playerLabel} ` : '';
  return `${icon} ${prefix}${label}${detail ? ` ${detail}` : ''}`;
}

// ============================================================
// Helper: format duration
// ============================================================

function formatDuration(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${String(m)}:${String(s).padStart(2, '0')}`;
}

// ============================================================
// Board Visualization Sub-Component
// ============================================================

interface BoardUnit {
  cardName: string;
  atk: number | null;
  playerId: string;
  row: string;
  column: number;
}

function buildBoardState(events: StoredEvent[], upToIndex: number, replayData: ReplayData | null): { p1Units: (BoardUnit | null)[]; p1Back: (BoardUnit | null)[]; p2Units: (BoardUnit | null)[]; p2Back: (BoardUnit | null)[] } {
  const p1Id = replayData?.players.player1_id ?? '';
  // 5 columns per row, two rows per player
  const board: Map<string, BoardUnit> = new Map();

  // Helper: extract position row/column from unknown data
  const getPosRow = (pos: Record<string, unknown>, fallback = 'front'): string =>
    (pos.row as string | undefined) ?? (pos.zone as string | undefined) ?? fallback;
  const getPosCol = (pos: Record<string, unknown>, fallback = 0): number =>
    (pos.column as number | undefined) ?? (pos.col as number | undefined) ?? (pos.index as number | undefined) ?? fallback;

  // Replay events up to current index to build board state
  for (let i = 0; i <= upToIndex && i < events.length; i++) {
    const event = events[i];
    const data = event.event_data as Record<string, unknown> | null;
    if (!data) continue;

    const cardId = (data.card_id ?? data.cardId ?? '') as string;
    const cardName = (data.card_name ?? data.cardName ?? data.name ?? '') as string;
    const position = data.position as Record<string, unknown> | undefined;

    switch (event.event_type) {
      case 'CARD_SUMMON':
      case 'unit_summoned':
      case 'summon_unit': {
        if (cardId && position) {
          const row = getPosRow(position);
          const col = getPosCol(position);
          const atk = (data.atk ?? data.attack ?? null) as number | null;
          board.set(cardId, {
            cardName: cardName || cardId.slice(0, 6),
            atk,
            playerId: event.player_id,
            row,
            column: col,
          });
        }
        break;
      }
      case 'UNIT_DESTROYED':
      case 'unit_destroyed':
      case 'CARD_DESTROY':
        if (cardId) board.delete(cardId);
        break;
      case 'CARD_MOVE':
      case 'unit_moved':
      case 'move_unit': {
        if (cardId && position) {
          const existing = board.get(cardId);
          if (existing) {
            existing.row = getPosRow(position, existing.row);
            existing.column = getPosCol(position, existing.column);
          }
        }
        break;
      }
      case 'CARD_DISCARD':
      case 'card_discarded':
        if (cardId) board.delete(cardId);
        break;
    }
  }

  // Split into player rows
  const p1Front: (BoardUnit | null)[] = [null, null, null, null, null];
  const p1Back: (BoardUnit | null)[] = [null, null, null, null, null];
  const p2Front: (BoardUnit | null)[] = [null, null, null, null, null];
  const p2Back: (BoardUnit | null)[] = [null, null, null, null, null];

  for (const unit of board.values()) {
    const isP1 = unit.playerId === p1Id;
    const col = Math.max(0, Math.min(4, unit.column - 1)); // 1-indexed → 0-indexed, clamp
    const isFront = unit.row === 'front' || unit.row === 'battlefield';
    const isBack = unit.row === 'back';

    if (isP1) {
      if (isFront) p1Front[col] = unit;
      else if (isBack) p1Back[col] = unit;
      else p1Front[col] = unit; // default to front
    } else {
      if (isFront) p2Front[col] = unit;
      else if (isBack) p2Back[col] = unit;
      else p2Front[col] = unit;
    }
  }

  return { p1Units: p1Front, p1Back, p2Units: p2Front, p2Back };
}

function BoardVisualization({ events, currentIndex, replayData }: { events: StoredEvent[]; currentIndex: number; replayData: ReplayData | null }) {
  const { p1Units, p1Back, p2Units, p2Back } = buildBoardState(events, currentIndex, replayData);
  const p1Id = replayData?.players.player1_id ?? 'P1';
  const p2Id = replayData?.players.player2_id ?? 'P2';

  const renderRow = (units: (BoardUnit | null)[], label: string) => (
    <div style={boardStyles.row}>
      <span style={boardStyles.rowLabel}>{label}</span>
      {units.map((unit, col) => (
        <div key={col} style={unit ? boardStyles.unitCell : boardStyles.emptyCell}>
          {unit ? (
            <div style={boardStyles.unitContent}>
              <div style={boardStyles.unitName}>{unit.cardName.length > 4 ? unit.cardName.slice(0, 4) + '…' : unit.cardName}</div>
              {unit.atk !== null && <div style={boardStyles.unitAtk}>ATK {String(unit.atk)}</div>}
            </div>
          ) : (
            <span style={boardStyles.emptyText}>—</span>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div style={boardStyles.container}>
      {/* P2 (opponent, top) */}
      <div style={boardStyles.playerSection}>
        <div style={boardStyles.playerLabel}>🔴 {p2Id.slice(0, 8)}</div>
        {renderRow(p2Back, '後排')}
        {renderRow(p2Units, '前排')}
      </div>

      {/* Divider */}
      <div style={boardStyles.divider}>
        <span style={boardStyles.dividerText}>── 戰場中線 ──</span>
      </div>

      {/* P1 (self, bottom) */}
      <div style={boardStyles.playerSection}>
        {renderRow(p1Units, '前排')}
        {renderRow(p1Back, '後排')}
        <div style={boardStyles.playerLabel}>🔵 {p1Id.slice(0, 8)}</div>
      </div>
    </div>
  );
}

// ============================================================
// Main ReplayViewer Component
// ============================================================

export function ReplayViewer() {
  const { goBack } = useUIStore();
  const {
    isLoading,
    error,
    replayData,
    currentEventIndex,
    isPlaying,
    playbackSpeed,
    currentTurn,
    totalEvents,
    totalTurns,
    progress,
    togglePlay,
    stepForward,
    stepBackward,
    jumpToStart,
    jumpToEnd,
    setSpeed,
    setEventIndex,
    pause,
    reset,
  } = useReplayStore();

  const eventListRef = useRef<HTMLDivElement>(null);
  const currentEventRef = useRef<HTMLDivElement>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      pause();
    };
  }, [pause]);

  // Auto-scroll event list to current event
  useEffect(() => {
    if (currentEventRef.current && eventListRef.current) {
      currentEventRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [currentEventIndex]);

  // Handle back button
  const handleBack = useCallback(() => {
    reset();
    goBack();
  }, [reset, goBack]);

  // Handle progress bar click
  const handleProgressClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (totalEvents <= 1) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    const targetIndex = Math.round(ratio * (totalEvents - 1));
    setEventIndex(targetIndex);
  }, [totalEvents, setEventIndex]);

  // Jump to prev turn
  const handlePrevTurn = useCallback(() => {
    if (!replayData) return;
    const events = replayData.events;
    // Find first event of the previous turn
    const curTurn = events[currentEventIndex]?.turn_number ?? 0;
    const prevTurn = Math.max(1, curTurn - 1);
    const targetIndex = events.findIndex((e) => e.turn_number >= prevTurn);
    if (targetIndex >= 0) {
      setEventIndex(targetIndex);
    }
  }, [replayData, currentEventIndex, setEventIndex]);

  // Jump to next turn
  const handleNextTurn = useCallback(() => {
    if (!replayData) return;
    const events = replayData.events;
    const curTurn = events[currentEventIndex]?.turn_number ?? 0;
    const nextTurn = curTurn + 1;
    const targetIndex = events.findIndex((e) => e.turn_number >= nextTurn);
    if (targetIndex >= 0) {
      setEventIndex(targetIndex);
    } else {
      // Jump to end
      jumpToEnd();
    }
  }, [replayData, currentEventIndex, setEventIndex, jumpToEnd]);

  // Loading state
  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.centerMessage}>
          <div style={styles.spinner}>⏳</div>
          <div style={styles.loadingText}>載入重播資料中...</div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.centerMessage}>
          <div style={styles.errorIcon}>❌</div>
          <div style={styles.errorText}>{error}</div>
          <button style={styles.backButtonCenter} onClick={handleBack}>返回</button>
        </div>
      </div>
    );
  }

  // No data state
  if (!replayData) {
    return (
      <div style={styles.container}>
        <div style={styles.centerMessage}>
          <div style={styles.errorText}>無重播資料</div>
          <button style={styles.backButtonCenter} onClick={handleBack}>返回</button>
        </div>
      </div>
    );
  }

  const events = replayData.events;
  const result = replayData.result;
  const gameId = replayData.game_id;
  const p1Id = replayData.players.player1_id;
  const p2Id = replayData.players.player2_id;
  const isAtEnd = currentEventIndex >= totalEvents - 1;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backButton} onClick={handleBack}>← 返回</button>
        <h1 style={styles.title}>重播 Game #{gameId.slice(0, 8)}</h1>
        <div style={styles.headerInfo}>
          {result && (
            <>
              <span style={styles.headerDetail}>
                🔵 {p1Id.slice(0, 8)} vs 🔴 {p2Id.slice(0, 8)}
              </span>
              <span style={styles.headerDetail}>
                ⏱ {formatDuration(result.duration_secs)}
              </span>
              <span style={styles.headerDetail}>
                🏆 {result.winner_id
                  ? (result.winner_id === p1Id ? p1Id.slice(0, 8) : p2Id.slice(0, 8))
                  : '平手'}
                {result.win_condition ? ` (${WIN_CONDITION_MAP[result.win_condition] ?? result.win_condition})` : ''}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Board Visualization */}
      <div style={styles.boardArea}>
        <BoardVisualization
          events={events}
          currentIndex={currentEventIndex}
          replayData={replayData}
        />
      </div>

      {/* Result Banner (when at end) */}
      {isAtEnd && result && (
        <div style={styles.resultBanner}>
          <span style={styles.resultText}>
            🏁 遊戲結束 — {result.winner_id
              ? `${result.winner_id === p1Id ? p1Id.slice(0, 8) : p2Id.slice(0, 8)} 勝利！`
              : '平手！'}
            {result.win_condition ? ` (${WIN_CONDITION_MAP[result.win_condition] ?? result.win_condition})` : ''}
          </span>
        </div>
      )}

      {/* Transport Controls */}
      <div style={styles.controls}>
        <div style={styles.transportRow}>
          <button style={styles.transportButton} onClick={handlePrevTurn} title="上一回合">◀◀</button>
          <button style={styles.transportButton} onClick={stepBackward} title="上一事件">◀</button>
          <button
            style={{ ...styles.transportButton, ...styles.playButton }}
            onClick={togglePlay}
            title={isPlaying ? '暫停' : '播放'}
          >
            {isPlaying ? '⏸' : '▶'}
          </button>
          <button style={styles.transportButton} onClick={stepForward} title="下一事件">▶</button>
          <button style={styles.transportButton} onClick={handleNextTurn} title="下一回合">▶▶</button>

          {/* Speed Selector */}
          <div style={styles.speedGroup}>
            {[1, 2, 4].map((speed) => (
              <button
                key={speed}
                style={{
                  ...styles.speedButton,
                  backgroundColor: playbackSpeed === speed ? '#4a6a8a' : '#3a3a5a',
                }}
                onClick={() => { setSpeed(speed); }}
              >
                {speed}x
              </button>
            ))}
          </div>

          {/* Jump buttons */}
          <button style={styles.jumpButton} onClick={jumpToStart} title="跳到開頭">⏮</button>
          <button style={styles.jumpButton} onClick={jumpToEnd} title="跳到結尾">⏭</button>
        </div>

        {/* Progress Bar */}
        <div style={styles.progressRow}>
          <div style={styles.progressBar} onClick={handleProgressClick}>
            <div style={{ ...styles.progressFill, width: `${String(progress * 100)}%` }} />
            <div style={{ ...styles.progressThumb, left: `${String(progress * 100)}%` }} />
          </div>
          <span style={styles.turnIndicator}>
            T{currentTurn}/{totalTurns}
          </span>
        </div>

        <div style={styles.eventCounter}>
          事件 {currentEventIndex + 1} / {totalEvents}
        </div>
      </div>

      {/* Event List */}
      <div style={styles.eventPanel}>
        <div style={styles.eventPanelHeader}>📜 事件列表</div>
        <div style={styles.eventList} ref={eventListRef}>
          {events.map((event, index) => {
            const isCurrentEvent = index === currentEventIndex;
            const isPast = index < currentEventIndex;
            return (
              <div
                key={`${String(event.id)}-${String(index)}`}
                ref={isCurrentEvent ? currentEventRef : undefined}
                style={{
                  ...styles.eventItem,
                  backgroundColor: isCurrentEvent ? '#3a4a6a' : isPast ? '#252545' : '#2a2a4a',
                  borderLeftColor: isCurrentEvent ? '#6688cc' : 'transparent',
                  opacity: isPast ? 0.7 : 1,
                }}
                onClick={() => { setEventIndex(index); }}
              >
                <span style={styles.eventTurn}>T{event.turn_number}</span>
                <span style={styles.eventDesc}>
                  {formatEventDescription(event, replayData)}
                </span>
              </div>
            );
          })}
          {events.length === 0 && (
            <div style={styles.emptyEvents}>無事件資料</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Styles
// ============================================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100vw',
    height: '100vh',
    backgroundColor: '#1a1a2e',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Arial, sans-serif',
    color: '#ffffff',
    overflow: 'hidden',
  },
  centerMessage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: '16px',
  },
  spinner: {
    fontSize: '48px',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: '18px',
    color: '#aaaacc',
  },
  errorIcon: {
    fontSize: '48px',
  },
  errorText: {
    fontSize: '18px',
    color: '#ff8888',
  },
  backButtonCenter: {
    padding: '10px 24px',
    fontSize: '16px',
    backgroundColor: '#3a3a5a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    marginTop: '12px',
  },

  // Header
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 20px',
    backgroundColor: '#252545',
    borderBottom: '1px solid #3a3a5a',
    gap: '16px',
    flexShrink: 0,
  },
  backButton: {
    padding: '8px 16px',
    fontSize: '14px',
    backgroundColor: '#3a3a5a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  title: {
    fontSize: '20px',
    fontWeight: 'bold',
    margin: 0,
    whiteSpace: 'nowrap',
  },
  headerInfo: {
    display: 'flex',
    gap: '16px',
    marginLeft: 'auto',
    flexWrap: 'wrap',
  },
  headerDetail: {
    fontSize: '13px',
    color: '#aaaacc',
    whiteSpace: 'nowrap',
  },

  // Board
  boardArea: {
    flex: '0 0 auto',
    padding: '12px 20px',
    display: 'flex',
    justifyContent: 'center',
    overflow: 'auto',
  },

  // Result Banner
  resultBanner: {
    padding: '10px 20px',
    backgroundColor: '#3a4a3a',
    borderTop: '2px solid #66aa66',
    borderBottom: '2px solid #66aa66',
    textAlign: 'center',
    flexShrink: 0,
  },
  resultText: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#88ff88',
  },

  // Controls
  controls: {
    padding: '10px 20px',
    backgroundColor: '#252545',
    borderTop: '1px solid #3a3a5a',
    borderBottom: '1px solid #3a3a5a',
    flexShrink: 0,
  },
  transportRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  transportButton: {
    width: '40px',
    height: '36px',
    fontSize: '16px',
    backgroundColor: '#3a3a5a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  playButton: {
    width: '48px',
    height: '40px',
    fontSize: '20px',
    backgroundColor: '#4a6a4a',
  },
  speedGroup: {
    display: 'flex',
    gap: '4px',
    marginLeft: '16px',
  },
  speedButton: {
    padding: '6px 12px',
    fontSize: '12px',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  jumpButton: {
    width: '36px',
    height: '32px',
    fontSize: '14px',
    backgroundColor: '#3a3a5a',
    color: '#aaaacc',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginLeft: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  progressBar: {
    flex: 1,
    height: '8px',
    backgroundColor: '#3a3a5a',
    borderRadius: '4px',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'visible',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#6688cc',
    borderRadius: '4px',
    transition: 'width 0.15s ease',
  },
  progressThumb: {
    position: 'absolute',
    top: '-4px',
    width: '16px',
    height: '16px',
    backgroundColor: '#8899dd',
    borderRadius: '50%',
    transform: 'translateX(-50%)',
    transition: 'left 0.15s ease',
    boxShadow: '0 0 4px rgba(100,120,200,0.5)',
  },
  turnIndicator: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#aaaacc',
    whiteSpace: 'nowrap',
    minWidth: '60px',
    textAlign: 'right',
  },
  eventCounter: {
    textAlign: 'center',
    fontSize: '11px',
    color: '#666688',
    marginTop: '4px',
  },

  // Event Panel
  eventPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
  },
  eventPanelHeader: {
    padding: '8px 20px',
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#aaaacc',
    backgroundColor: '#222244',
    flexShrink: 0,
  },
  eventList: {
    flex: 1,
    overflowY: 'auto',
    padding: '4px 12px',
  },
  eventItem: {
    padding: '6px 10px',
    marginBottom: '2px',
    borderRadius: '4px',
    borderLeft: '3px solid transparent',
    cursor: 'pointer',
    display: 'flex',
    gap: '10px',
    fontSize: '13px',
    transition: 'background-color 0.15s',
  },
  eventTurn: {
    fontSize: '11px',
    fontWeight: 'bold',
    color: '#8888aa',
    minWidth: '28px',
    flexShrink: 0,
  },
  eventDesc: {
    flex: 1,
    lineHeight: '1.4',
  },
  emptyEvents: {
    textAlign: 'center',
    color: '#666666',
    padding: '40px',
    fontSize: '14px',
  },
};

const boardStyles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    width: '100%',
    maxWidth: '600px',
  },
  playerSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  playerLabel: {
    fontSize: '13px',
    fontWeight: 'bold',
    color: '#aaaacc',
    padding: '2px 8px',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  rowLabel: {
    fontSize: '11px',
    color: '#666688',
    width: '36px',
    textAlign: 'right',
    flexShrink: 0,
  },
  unitCell: {
    flex: 1,
    height: '48px',
    backgroundColor: '#2a3a4a',
    borderRadius: '4px',
    border: '1px solid #4a5a6a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2px',
  },
  emptyCell: {
    flex: 1,
    height: '48px',
    backgroundColor: '#222234',
    borderRadius: '4px',
    border: '1px solid #333344',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  unitContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1px',
  },
  unitName: {
    fontSize: '11px',
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
    lineHeight: '1.2',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '80px',
  },
  unitAtk: {
    fontSize: '10px',
    color: '#ff8866',
    fontWeight: 'bold',
  },
  emptyText: {
    color: '#444455',
    fontSize: '14px',
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 0',
  },
  dividerText: {
    fontSize: '11px',
    color: '#555566',
    letterSpacing: '2px',
  },
};
